import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { useSessionJwt } from '../hooks/useSessionJwt';
import { Breadcrumb, CopyButton } from '../components';
import ViewTabs from '../components/ViewTabs';
import SessionChatStrip from '../components/SessionChatStrip';
import TraceViewer from './trace-detail/TraceViewer';
import { useTimeRange } from '../contexts/TimeRangeContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useApp } from '../contexts/AppContext';

export default function TraceDetail() {
  const { selectedAppId, traceId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session } = useStytchMemberSession();
  const { getJwt } = useSessionJwt();
  const { selectedRange } = useTimeRange();
  const { selectedEnvironment, environments } = useEnvironment();
  const { selectedAppName } = useApp();

  const urlTab = searchParams.get('tab') || 'outline';
  const requestedSpanId = searchParams.get('span') || '';
  const [activeTab, setActiveTab] = useState(urlTab);
  const [signalsPortalTarget, setSignalsPortalTarget] = useState(null);

  const traceFromState = location.state?.trace || null;

  // Allows switching traces in-place (via SessionChatStrip) without remounting
  const [activeTraceId, setActiveTraceId] = useState(null);
  const [overrideTrace, setOverrideTrace] = useState(null);

  // Surfaced from TraceViewer once spans/detail resolve the session_id
  const [sessionId, setSessionId] = useState(null);

  const currentTraceId = activeTraceId || traceId;
  const currentTrace = overrideTrace || traceFromState;

  const tenantId = session?.organization_id || null;

  // Sync activeTab with URL when URL changes externally
  useEffect(() => {
    setActiveTab(searchParams.get('tab') || 'outline');
  }, [searchParams]);

  const handleTraceChange = useCallback((newTraceId, trace) => {
    if (newTraceId === currentTraceId) return;
    setActiveTraceId(newTraceId);
    setOverrideTrace(trace || null);
    const params = new URLSearchParams();
    if (selectedEnvironment) params.set('env', selectedEnvironment);
    if (selectedRange) params.set('range', selectedRange);
    if (activeTab && activeTab !== 'outline') params.set('tab', activeTab);
    window.history.replaceState(null, '', `/app/${selectedAppId}/traces/${encodeURIComponent(newTraceId)}?${params.toString()}`);
  }, [currentTraceId, selectedEnvironment, selectedRange, activeTab, selectedAppId]);

  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
    const params = new URLSearchParams();
    if (selectedEnvironment) params.set('env', selectedEnvironment);
    if (selectedRange) params.set('range', selectedRange);
    params.set('tab', newTab);
    if (requestedSpanId) params.set('span', requestedSpanId);
    navigate(`/app/${selectedAppId}/traces/${currentTraceId}?${params.toString()}`, { replace: true, state: location.state });
  }, [selectedEnvironment, selectedRange, requestedSpanId, navigate, selectedAppId, currentTraceId, location.state]);

  const appName = useMemo(() => {
    if (selectedAppName) return selectedAppName;
    if (selectedAppId && environments.length) {
      for (const env of environments) {
        const app = env.apps?.find(a => a.id === selectedAppId);
        if (app) return app.name;
      }
    }
    return selectedAppId;
  }, [selectedAppId, selectedAppName, environments]);

  const breadcrumbItems = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedEnvironment) params.set('env', selectedEnvironment);
    if (selectedRange) params.set('range', selectedRange);
    const queryString = params.toString() ? `?${params.toString()}` : '';

    const telemetryParams = new URLSearchParams(params);
    telemetryParams.set('tab', 'telemetry');
    telemetryParams.set('subtab', 'traces');

    const stateBreadcrumbItems = Array.isArray(location.state?.referrerBreadcrumbItems)
      ? location.state.referrerBreadcrumbItems.filter(item => item?.label)
      : [];
    if (stateBreadcrumbItems.length > 0) {
      return [
        ...stateBreadcrumbItems,
        { label: currentTraceId?.slice(0, 8) },
      ];
    }

    const referrerUrl = location.state?.referrerUrl || `/app/${selectedAppId}?${telemetryParams.toString()}`;
    const referrerLabel = location.state?.referrerLabel || 'Traces';

    return [
      { label: appName, to: `/app/${selectedAppId}${queryString}` },
      { label: referrerLabel, to: referrerUrl },
      { label: currentTraceId?.slice(0, 8) },
    ];
  }, [selectedAppId, appName, selectedEnvironment, selectedRange, location.state, currentTraceId]);

  const leftPanel = (
    <SessionChatStrip
      sessionId={sessionId}
      currentTraceId={currentTraceId}
      appId={selectedAppId}
      sessionJwt={getJwt()}
      tenantId={tenantId}
      onTraceChange={handleTraceChange}
    />
  );

  return (
    <div className="flex h-full flex-1 flex-col space-y-6">
      <Breadcrumb items={breadcrumbItems} />

      <div className="flex min-h-0 flex-1 flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-text-primary">Trace {currentTraceId?.slice(0, 8)}</h1>
              <CopyButton text={currentTraceId} label="Copy Trace ID" />
            </div>
            <ViewTabs
              tabs={[
                { value: 'outline', label: 'Outline' },
                { value: 'timeline', label: 'Timeline' },
                { value: 'graph', label: 'Graph' },
                { value: 'agents', label: 'Business Tasks' },
              ]}
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />
            <div
              ref={setSignalsPortalTarget}
              className="min-w-[280px] flex-1 empty:hidden"
            />
          </div>
        </div>

        <TraceViewer
          traceId={currentTraceId}
          appId={selectedAppId}
          initialTrace={currentTrace}
          activeTab={activeTab}
          requestedSpanId={requestedSpanId}
          onSessionId={setSessionId}
          signalsPortalTarget={signalsPortalTarget}
          leftPanel={leftPanel}
        />
      </div>
    </div>
  );
}
